/**
 * List persisted server log sessions.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Radio, Trash2 } from "lucide-react";
import type { SessionLogMeta } from "@memes/shared";
import { devLogsApi } from "../../lib/api";

function formatDuration(startedAt: string, endedAt: string | null): string {
  const end = endedAt ? new Date(endedAt).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((end - new Date(startedAt).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function SessionRow({ s }: { s: SessionLogMeta }): React.ReactElement {
  const deleteSession = async (): Promise<void> => {
    if (!confirm("Delete this log session?")) return;
    await devLogsApi.deleteSession(s.id);
    window.location.reload();
  };

  return (
    <tr className="border-b border-gray-100 dark:border-gray-800">
      <td className="px-3 py-2 text-sm">
        {s.active ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-950 dark:text-green-200">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
            Active
          </span>
        ) : (
          <span className="text-xs text-gray-400">Ended</span>
        )}
      </td>
      <td className="px-3 py-2 text-sm tabular-nums">
        {new Date(s.startedAt).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-sm text-gray-500">{formatDuration(s.startedAt, s.endedAt)}</td>
      <td className="px-3 py-2 text-sm tabular-nums">{s.lineCount}</td>
      <td className="px-3 py-2 text-sm">
        {s.errorCount > 0 ? (
          <span className="text-red-600 dark:text-red-400">{s.errorCount} err</span>
        ) : (
          "—"
        )}
        {s.warnCount > 0 ? (
          <span className="ml-2 text-amber-600 dark:text-amber-400">{s.warnCount} warn</span>
        ) : null}
      </td>
      <td className="px-3 py-2 text-xs text-gray-500">v{s.version}</td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-2">
          <Link
            to={s.active ? "/dev/logs/live" : `/dev/logs/${s.id}`}
            className="btn text-xs"
          >
            {s.active ? "Live" : "View"}
          </Link>
          {!s.active ? (
            <button type="button" className="btn p-1.5 text-red-600" onClick={() => void deleteSession()}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

export const DevLogSessions: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dev", "log-sessions"],
    queryFn: () => devLogsApi.listSessions(),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Log sessions</h2>
        <Link to="/dev/logs/live" className="btn btn-primary inline-flex items-center gap-1.5">
          <Radio className="h-4 w-4" />
          Live tail
        </Link>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading sessions…
        </div>
      ) : null}
      {error ? (
        <p className="text-sm text-red-600">
          Failed to load sessions. Set your Dev key above if on production.
        </p>
      ) : null}

      {data?.sessions.length ? (
        <div className="card overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500 dark:bg-gray-900">
              <tr>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Started</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Lines</th>
                <th className="px-3 py-2">Issues</th>
                <th className="px-3 py-2">Ver</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.map((s) => (
                <SessionRow key={s.id} s={s} />
              ))}
            </tbody>
          </table>
        </div>
      ) : !isLoading ? (
        <p className="text-sm text-gray-500">No log sessions yet.</p>
      ) : null}
    </div>
  );
};
